import { computed, createModel, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

export const notepadWidgetStateSchema = z.object({
    content: z.string(),
});

export type NotepadWidgetState = z.infer<typeof notepadWidgetStateSchema>;

export const NotepadSelfOwnedModel = createModel(
    (init: {
        container: WidgetContainerController;
        tabId: WidgetId;
        persisted: NotepadWidgetState;
        logger: Logger;
    }) => {
        const channelSlice = new WidgetContainerChannelSlice({ container: init.container, tabId: init.tabId });
        const widgetTabSlice = new WidgetContainerTabSlice({
            container: init.container,
            tabId: init.tabId,
            logger: init.logger,
        });

        const content = signal(init.persisted.content);
        const dirty = signal(false);

        const eventBus = MergedEventBus.from(channelSlice.eventBus).merge(widgetTabSlice.eventBus);

        const contentReadonly: ReadonlySignal<string> = computed(() => content.value);
        const dirtyReadonly: ReadonlySignal<boolean> = computed(() => dirty.value);

        return {
            content: contentReadonly,
            dirty: dirtyReadonly,
            eventBus,
            setContent(value: string): void {
                if (content.value === value) return;
                content.value = value;
                dirty.value = true;
            },
            toJSON(): NotepadWidgetState {
                return { content: content.value };
            },
            markClean(): void {
                dirty.value = false;
            },
        };
    },
);

export type NotepadSelfOwnedModel = InstanceType<typeof NotepadSelfOwnedModel>;
